import { AgGridReact } from "ag-grid-react";
import {
  Button,
  Dropdown,
  MenuProps,
  Modal,
  Tag,
  message,
  notification
} from "antd";
import CiqAgSearch from "components/ciq-ag-search";
import CiqGridViewWrapper from "components/ciq-grid-view-wrapper";
import AddEntityLogButton from "components/add-entity-log-button";
import ImportFeatureLogs from "components/import-feature-logs";
import CollapsibleSidebarLayout from "components/collapsible-sidebar-layout/collapsible-sidebar-layout";
import FeatureCommentsTabComponent from "components/feature-activity-card/feature-comments-tab";
import { useCommentsCountLabel } from "components/feature-activity-card/use-comments-count-label";
import { DownOutlined, CloseOutlined, DeleteOutlined } from "@ant-design/icons";
import BlankState, {
  GridNoRowsOverlay,
  GridNoRowsOverlayContext
} from "components/blank-state";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { calculateLogPageStats } from "components/ciq-log-page-header";
import { getGridStatusBudge } from "components/ciq-log-page-header/grid-status-budge";
import { newEntityNotificationMessage } from "entity-app/shared-components/new-entity-notification-message";
import { useParams } from "react-router";
import {
  FilterChipComponent,
  FilterChipComponentRef
} from "entity-app/shared-components/log-render-components/filter-chip-component";
import { AppContext } from "context/AppProvider";
import { applyBidPackagesLogFilters } from "utils/ag-grid-programmatic-filtering";
import { resetGridFilters } from "entity-app/utils/utils";
import { FeatureTypes } from "entity-app/constants";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import { exportEntityLogAsCSV } from "entity-app/utils/export-entity-log-as-csv";
import {
  exportEntityLogAsExcel,
  sanitizeSheetName
} from "components/excel-ag-grid-export/export-entity-log-as-excel";
import { excelStyles } from "components/excel-ag-grid-export/excel-style";
import { ExcelExportOptions } from "components/excel-ag-grid-export/excel-export-option";
import ExportOptionsPopover, {
  ExportFormatOption,
  ExportSelection
} from "components/export-options-popover";
import PrintViewValidationModal from "pages/print-view-layout/Print-View-Validation-Modal";
import {
  BidPackageLogPrintViewState,
  PRINT_VIEW_STORAGE_KEY
} from "pages/print-view-layout/model";
import { DateUtils } from "utils/dateutils";

import {
  EFeaturePageId,
  ErrorMessages,
  InfoMessages,
  ProjectPermissionEnum,
  SidepanelStyles,
  SuccessMessages
} from "constants/index";
import { UtilColumnConfig } from "components/column-config-views/column-config-util";
import ColumnConfigViews, {
  TSaveColumnState
} from "components/column-config-views";
import ErrorBoundary from "components/error-boundary";
import { getRowId } from "pages/submittals-log-page/utils";
import { updateGridSelectionOnFilter } from "utils/utils";
import { InfoFeatureType } from "change-events/change-event-polling";
import { CiqIconBox } from "components/svg-icons";
import { useFeatureLogV2InlineEdit } from "entity-app/hooks/feature-log-v2-inline-edit";
import {
  getMaxNumberOfMilestone,
  getSidebarColumnLayoutForBidDesignPackages
} from "entity-app/shared-components/entity-milestones-duration-columns";
import { ColDef, ColGroupDef } from "ag-grid-enterprise";
import { type DsmScopeItem } from "design-scope";
import { groupScopeItemsByBid } from "pages/design-scope-page/shared/scope-context";
import { BidScopeListDrawer } from "pages/design-scope-page/shared/bid-scope-list-drawer";
import { useDesignScopeList } from "pages/design-scope-page/hooks/use-design-scope-list";
import { toModalScopeItem } from "pages/design-scope-page/shared/design-scope-list-mappers";
import useFeatureLogNavWriter from "hooks/use-feature-log-nav-writer";
import { usePreconstrutionData } from "./hooks/preconstruction-data";
import CreateBidPackageComponent from "./components/create-bid-package";
import BulkEditBidPackageDetails from "./components/bulk-edit-details";
import BulkEditOnsiteEstimateModal from "./components/bulk-edit-onsite-estimate";
import {
  ONSITE_ESTIMATE_COL_ID,
  ONSITE_ESTIMATE_DEFAULT_LABEL,
  withOnsiteEstimateColumn
} from "./onsite-estimate";
import BulkFeatureEditStatus from "components/bulk-edit-status/bulk-feature-edit-status";
import { mapPackageRowToWorkflowItem } from "components/bulk-edit-status/derive-next-milestone";
import BulkEditDeadlineDateModal from "components/bulk-edit-dateline-date/bulk-edit-dateline-date";
import FeatureBulkEditDurationsNewModel from "components/feature-bulk-edit-durations/feature-bulk-edit-durations-new-model";
import { mapPackageRowToDurationRow } from "components/feature-bulk-edit-durations/map-package-row-to-duration-row";
import BidPackageCiqIcon from "components/svg-icons/bid-package-ciq-icon";
import { usePreconstrutionAgGrid } from "./hooks/preconstruction-ag-grid-data";
import useAuditEventLog from "entity-app/utils/export-audit-log";
import { getConditionalFormattingHeaderMenuItems } from "components/conditional-formatting/conditional-formatting-header-menu";
import type { ConditionalFormattingRule } from "components/conditional-formatting";
import { useConditionalFormatting } from "components/conditional-formatting/use-conditional-formatting";
import { RiskLevelType } from "components/date-block/models";
import {
  PreconstructionLogProvider,
  usePreconstructionLogContext
} from "./preconstruction-log-context";
import "./preconstruction-log-page.scss";

const featureKey = "bid-packages"; // URL

function PreconstructionLogSidebarHeader({
  title,
  onClose,
  caption
}: {
  title: string;
  onClose: () => void;
  caption: string;
}) {
  return (
    <div
      className="flex items-center justify-between"
      style={{ borderBottom: "1px solid var(--ciq-gray-950-a30)" }}
    >
      <div
        className="p-4 flex items-center overflow-hidden"
        data-testid="bid-package-log-sidebar-header"
      >
        <span className="shrink-0 sidepanel-header-title">{title}</span>
        <span className="shrink-0 text-xs font-normal ml-2 mr-2">|</span>
        <span
          className="text-xs font-normal truncate min-w-0"
          title={caption || ""}
        >
          {caption || ""}
        </span>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="border-none bg-transparent cursor-pointer p-2"
      >
        <CloseOutlined />
      </button>
    </div>
  );
}

function PreconstructionLogPageContent() {
  const {
    isSidebarOpen,
    setIsSidebarOpen,
    sidebarMetadata,
    setSidebarMetadata
  } = usePreconstructionLogContext();
  const { itemDetails } = sidebarMetadata || {};
  const gridRef = useRef<AgGridReact<any>>(null);
  const {
    projectDetails,
    tokenContents,
    gqlClientForProject,
    isFeatureFlagEnabled,
    projectToken,
    tokenRetrievalState,
    isProjectArchived
  } = useContext(ProjectContext);
  const { saveAuditLogEvent } = useAuditEventLog(gqlClientForProject);

  const cannotWriteAccessOfBidPackage = isPermissionNotGrantted(
    ProjectPermissionEnum.BidPackageWrite,
    tokenContents?.role ?? ""
  );
  const conditionalFormattingRulesRef = useRef<ConditionalFormattingRule[]>([]);
  const pageData = usePreconstrutionData({
    gridRef,
    getConditionalsFormatting: () => conditionalFormattingRulesRef.current
  });
  const { calculateMaxNumberOfMilestone } = pageData;
  const agGridData = usePreconstrutionAgGrid({
    featureKey,
    milestonesColumns: pageData.milestonesColumns,
    cannotWriteAccessOfBidPackage,
    bidPackageHeader: pageData.bidPackageHeaderMap
  });

  const {
    conditionalFormattingRules,
    conditionalFormattingDefaultColDef,
    conditionalFormattingExcelStyles,
    setConditionalFormattingRules
  } = useConditionalFormatting({
    gridRef,
    defaultColDef: agGridData.defaultColDef
  });
  conditionalFormattingRulesRef.current = conditionalFormattingRules;
  const mergedExcelStyles = useMemo(
    () => [...excelStyles, ...conditionalFormattingExcelStyles],
    [conditionalFormattingExcelStyles]
  );

  const { onCellEditRequest } = useFeatureLogV2InlineEdit({
    refetchListData: pageData.refetchBidPackageList,
    infoFeatureType: InfoFeatureType.bidPackage
  });
  const filterChipRef = useRef<FilterChipComponentRef>(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showImportLogDrawer, setShowImportLogDrawer] = useState(false);
  const [api, contextHolder] = notification.useNotification();
  const { projectId } = useParams() as any;
  const { bidPackageListFilter, setBidPackageListFilter } =
    useContext(AppContext);

  // Held in a ref (not state) because AG Grid's `context` prop is copied by
  // reference and never triggers a cellRenderer re-invoke on its own — we
  // drive re-renders explicitly via refreshCells below, and the getter reads
  // whatever is current in the ref at call time.
  const scopeItemsByBidRef = useRef<Record<string, DsmScopeItem[]>>({});
  const [scopeCtx, setScopeCtx] = useState<{
    bidPackageId: string;
    label: string;
  } | null>(null);
  const { items: scopeListItems } = useDesignScopeList(
    projectToken || tokenRetrievalState?.token
  );

  useEffect(() => {
    scopeItemsByBidRef.current = groupScopeItemsByBid(
      scopeListItems.map(toModalScopeItem)
    );
    gridRef.current?.api?.refreshCells?.({
      columns: ["designScope"],
      force: true
    });
  }, [scopeListItems]);

  const navWriter = useFeatureLogNavWriter({
    gridRef,
    feature: "bid-packages",
    projectId
  });

  const isPrintViewEnabled = isFeatureFlagEnabled("PRINT_VIEW");
  const isExcelExportEnabled = isFeatureFlagEnabled("EXCEL_EXPORT");

  const [isFirstDataRendered, setIsFirstDataRendered] = useState(false);
  const isFirstDataRenderedRef = useRef(false);
  isFirstDataRenderedRef.current = isFirstDataRendered;
  const onSaveColumnStateRef = useRef(pageData.onSaveColumnState);
  onSaveColumnStateRef.current = pageData.onSaveColumnState;
  const firstDataRenderedTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(firstDataRenderedTimerRef.current), []);
  const [searchText, setSearchText] = useState("");
  const [statsData, setStatsData] = useState<{
    displayCount: number;
    filterName: undefined | string;
  }>();
  const [selectedRows, setSelectedRows] = useState<Array<any> | null>(null);
  const [itemSourceRowIndexForSidepanel, setItemSourceRowIndexForSidepanel] =
    useState<number | null>(null);

  const sidebarAction = useCallback(
    (params: {
      feature: string;
      action: "open" | "close";
      itemDetailsToSet: any | null;
    }) => {
      if (params.action === "open") {
        setIsSidebarOpen(true);
        setSidebarMetadata({
          feature: params.feature,
          itemDetails: params.itemDetailsToSet
        });
      } else {
        setIsSidebarOpen(false);
        setSidebarMetadata(null);
      }
    },
    [setIsSidebarOpen, setSidebarMetadata]
  );

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isSidebarOpen) {
        sidebarAction({ feature: "", action: "close", itemDetailsToSet: null });
      }
    };
    document.addEventListener("keydown", handleEscKey);
    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [isSidebarOpen, sidebarAction]);

  const isAttachementFeatureEnabled =
    !isFeatureFlagEnabled("DISABLE_ATTACHMENT");

  const { label: sidebarTitle, onCommentsCountChange } = useCommentsCountLabel(
    sidebarMetadata?.itemDetails?.id
  );

  const permissionsForComments = useMemo(
    () => (isProjectArchived ? [] : ["create_comment"]),
    [isProjectArchived]
  );

  const entityList = useMemo(
    () => pageData.bidPackageList?.bid_package_list_func ?? [],
    [pageData.bidPackageList?.bid_package_list_func]
  );

  const onCommentClick = useCallback(
    ({ sourceRowIndex }: { sourceRowIndex: number | undefined | null }) => {
      if (sourceRowIndex === undefined || sourceRowIndex === null) return;
      sidebarAction({
        feature: "comments",
        action: "open",
        itemDetailsToSet: entityList[sourceRowIndex]
      });
      setItemSourceRowIndexForSidepanel(sourceRowIndex);
    },
    [entityList, sidebarAction]
  );

  useEffect(() => {
    if (!isSidebarOpen) return;
    if (itemSourceRowIndexForSidepanel !== null) {
      const item = entityList[itemSourceRowIndexForSidepanel];
      if (!item) return;
      sidebarAction({
        feature: "comments",
        action: "open",
        itemDetailsToSet: item
      });
    }
  }, [
    itemSourceRowIndexForSidepanel,
    entityList,
    sidebarAction,
    isSidebarOpen
  ]);

  useEffect(() => {
    if (!isSidebarOpen) {
      setItemSourceRowIndexForSidepanel(null);
    }
  }, [isSidebarOpen]);

  const rowClassRules = useMemo(
    () => ({
      "selected-item-for-panel": (params: any) => {
        if (params.node?.group) return false;
        return params.data?.id === itemDetails?.id;
      }
    }),
    [itemDetails?.id]
  );

  const applySidebarTreeLayout = useCallback(() => {
    if (!gridRef.current?.api) return;
    const maxInfo = getMaxNumberOfMilestone({ api: gridRef.current.api });
    const milestonesCount = maxInfo?.numberOfMaxMileStones ?? 0;
    const offsetsCount = maxInfo?.numberOfMaxOffsets ?? 0;
    const { sidebarColumnLayout } = getSidebarColumnLayoutForBidDesignPackages(
      milestonesCount,
      offsetsCount
    );
    if (typeof gridRef.current.api.getToolPanelInstance !== "function") return;
    const columnsToolPanel =
      gridRef.current.api.getToolPanelInstance?.("columns");
    if (!columnsToolPanel) return;
    const existingLayout =
      (gridRef.current.api.getColumnDefs() as (ColDef | ColGroupDef)[]) || [];
    const filteredExistingLayout = existingLayout.filter(
      (item: ColDef | ColGroupDef) => {
        const colId = (item as ColDef).colId;
        if (!colId) return true;
        if (colId === "_milestones" || colId === "_durations") return false;
        if (/^m[1234]_\d+$/.test(colId)) return false;
        if (/^du[12]_\d+$/.test(colId)) return false;
        return true;
      }
    );
    columnsToolPanel.setColumnLayout([
      ...filteredExistingLayout,
      ...sidebarColumnLayout
    ]);
    const toolPanelState = (columnsToolPanel as any).getState?.();
    if (toolPanelState) {
      (columnsToolPanel as any).setState?.({
        ...toolPanelState,
        expandedGroupIds: (toolPanelState as any).expandedGroupIds?.filter(
          (id: string) => id !== "milestones_group" && id !== "durations_group"
        )
      });
    }
    (columnsToolPanel as any).collapseColumnGroups?.([
      "milestones_group",
      "durations_group"
    ]);
  }, []);

  const refreshSidebarTreeLayout = useCallback(() => {
    calculateMaxNumberOfMilestone();
    // Re-apply after grid/state updates so tool panel doesn't fall back to flat list
    setTimeout(() => {
      applySidebarTreeLayout();
    }, 0);
  }, [applySidebarTreeLayout, calculateMaxNumberOfMilestone]);

  const [bulkEditMode, setBulkEditMode] = useState<
    | "details"
    | "deadline_date"
    | "onsite_estimate"
    | "durations"
    | "status"
    | null
  >(null);

  // Bid package rows carry flat `milestones` / `offsets` arrays; the shared
  // durations editor expects the material-shaped nested `date_block`.
  const durationRows = useMemo(
    () => (selectedRows ?? []).map(mapPackageRowToDurationRow),
    [selectedRows]
  );

  const openNotification = (id: string, title: string) => {
    api.open(
      newEntityNotificationMessage({
        projectId,
        id,
        title,
        featureKey
      })
    );
  };

  const applyColumnConfig = useCallback(
    (columnStates: TSaveColumnState) => {
      if (!gridRef.current) return;
      UtilColumnConfig.setFilterStateFromData({
        columnStates: withOnsiteEstimateColumn(columnStates),
        gridRef: {
          api: gridRef.current.api
        }
      });
      setSearchText(columnStates?.quickSearch ?? "");
      const nextRules = columnStates?.conditionals_formatting ?? [];
      conditionalFormattingRulesRef.current = nextRules;
      setConditionalFormattingRules(nextRules);
    },
    [setConditionalFormattingRules]
  );

  const onConditionalFormattingRulesChange = useCallback(
    (rules: ConditionalFormattingRule[]) => {
      conditionalFormattingRulesRef.current = rules;
      setConditionalFormattingRules(rules);
      if (isFirstDataRenderedRef.current && !bidPackageListFilter) {
        onSaveColumnStateRef.current();
      }
    },
    [bidPackageListFilter, setConditionalFormattingRules]
  );

  const onSearch = useCallback((text: string) => {
    setSearchText(text);
    gridRef.current?.api?.setGridOption("quickFilterText", text);
  }, []);

  const onFirstDataRendered = useCallback(() => {
    if (bidPackageListFilter) {
      applyBidPackagesLogFilters({ gridRef, bidPackageListFilter });
    }
    if (
      pageData.savedColumns?.bid_package_column_config &&
      bidPackageListFilter === null &&
      gridRef.current
    ) {
      applyColumnConfig(pageData.savedColumns.bid_package_column_config);
    }
    refreshSidebarTreeLayout();

    // onFirstDataRendered can fire again (e.g. when columns reload), so
    // supersede any pending timer to keep at most one in flight.
    clearTimeout(firstDataRenderedTimerRef.current);
    firstDataRenderedTimerRef.current = setTimeout(() => {
      // NOTE: To load initial data once in onFilterApplied method
      setIsFirstDataRendered(true);
    }, 500);

    navWriter.onFirstDataRendered();
  }, [
    applyColumnConfig,
    bidPackageListFilter,
    pageData,
    refreshSidebarTreeLayout,
    navWriter
  ]);

  const deadlineDateLabel =
    pageData.bidPackageHeaderMap?.manual_entry_date || "Deadline Date";
  const onsiteEstimateLabel =
    pageData.bidPackageHeaderMap?.[ONSITE_ESTIMATE_COL_ID] ||
    ONSITE_ESTIMATE_DEFAULT_LABEL;

  const bulkEditActionMenu: MenuProps = useMemo(
    () => ({
      items: [
        {
          type: "group",
          label: "Bulk Edit",
          key: "bulk-edit-group",
          children: [
            {
              key: "details",
              label: "Details",
              onClick: () => setBulkEditMode("details")
            },
            {
              key: "deadline_date",
              label: deadlineDateLabel,
              onClick: () => setBulkEditMode("deadline_date")
            },
            {
              key: "onsite_estimate",
              label: onsiteEstimateLabel,
              onClick: () => setBulkEditMode("onsite_estimate")
            },
            {
              key: "durations",
              label: "Durations",
              onClick: () => setBulkEditMode("durations")
            },
            {
              key: "status",
              label: "Status",
              onClick: () => setBulkEditMode("status")
            }
          ]
        }
      ]
    }),
    [deadlineDateLabel, onsiteEstimateLabel]
  );

  const deleteBidPackages = useCallback(() => {
    Modal.confirm({
      title: InfoMessages.deleteBidPackageConfirmTitle,
      content: "This can't be undone.",
      okText: "Delete",
      okButtonProps: { danger: true, className: "ciq-delete-btn" },
      cancelText: "Cancel",
      async onOk() {
        try {
          // Delete button only renders when selection is non-empty
          /* v8 ignore next */
          if (!selectedRows?.length) return;
          const deleteRes = await pageData.mutationDeleteBidPackage({
            variables: {
              feature_instance_ids: selectedRows.map((row) => row.id),
              feature_type_id: FeatureTypes.BID_PACKAGE
            }
          });
          if (deleteRes.data) {
            message.success(SuccessMessages.deleteSuccess);
            setSelectedRows(null);
            gridRef.current?.api?.deselectAll();
          }
          if (deleteRes.errors) {
            console.error(deleteRes.errors);
            message.error(ErrorMessages.deleteBidPackageFailed);
          }
        } catch (ex: any) {
          message.error(ex.message || ErrorMessages.deleteBidPackageFailed);
        }
      }
    });
  }, [pageData, selectedRows]);

  const bulkEditButton = useMemo(() => {
    if (!selectedRows) return null;
    const selectedCount = selectedRows.length;
    if (selectedCount < 1) return null;
    return (
      <div
        className="bg-ciq-gray-100 bg-opacity-70 py-1 px-4 flex items-center space-x-2 rounded-md"
        key="PreconstructionLogPage_BulkAction"
      >
        <div className="py-1 whitespace-nowrap">
          {selectedRows?.length} Selected
        </div>
        {selectedCount > 1 && (
          <Dropdown
            menu={bulkEditActionMenu}
            key="PreconstructionLogPage_bulkEdit"
            data-testid="index-dropdown-edit"
            disabled={cannotWriteAccessOfBidPackage}
          >
            <Button
              disabled={cannotWriteAccessOfBidPackage}
              title={
                cannotWriteAccessOfBidPackage
                  ? ErrorMessages.PermissionNotGranted
                  : "Edit"
              }
            >
              Edit ({selectedCount}) <DownOutlined />
            </Button>
          </Dropdown>
        )}
        <Button
          key="PreconstructionLogPage_Delete"
          onClick={deleteBidPackages}
          danger
          icon={<DeleteOutlined />}
          className="ciq-delete-btn"
          title={
            cannotWriteAccessOfBidPackage
              ? ErrorMessages.PermissionNotGranted
              : "Delete"
          }
          disabled={cannotWriteAccessOfBidPackage}
          data-testid="delete-button"
        >
          Delete
        </Button>
      </div>
    );
  }, [
    bulkEditActionMenu,
    cannotWriteAccessOfBidPackage,
    deleteBidPackages,
    selectedRows
  ]);

  const onAllBidPackagesClick = useCallback(() => {
    resetGridFilters({
      setListFilter: setBidPackageListFilter,
      api: gridRef.current?.api
    });
  }, [setBidPackageListFilter, gridRef]);

  const onExportCsv = () => {
    if (projectDetails && gridRef.current) {
      const filename = `Bid_Package_Log_${projectDetails.name}.csv`;
      exportEntityLogAsCSV({
        filename,
        gridRef: {
          api: gridRef.current!.api
        },
        includedColumns: ["description"],
        excludedColumns: ["_milestones", "_durations"]
      });

      saveAuditLogEvent({
        eventType: "BID_PACKAGE_LOG_EXPORT_CSV",
        featureType: "BID_PACKAGES",
        filename
      });
    }
  };

  const onExportExcel = (options: ExcelExportOptions) => {
    if (!gridRef.current || !projectDetails) return;
    const filename = `Bid_Package_Log_${sanitizeSheetName(
      projectDetails.name
    )}.xlsx`;
    exportEntityLogAsExcel({
      projectDetails,
      gridRef,
      filename,
      prependText: `${
        projectDetails.name
      } - Bid Package Log - Exported from ConstructivIQ on ${DateUtils.dateTimeObj().format(
        `M/D [at] h:mma [${DateUtils.dateTimeObj().format("z")}]`
      )}`,
      includedColumns: ["description"],
      excludedColumns: ["_milestones", "_durations"],
      conditionalsFormatting: conditionalFormattingRulesRef.current,
      options
    });

    saveAuditLogEvent({
      eventType: "BID_PACKAGE_LOG_EXPORT_EXCEL",
      featureType: "BID_PACKAGES",
      filename
    });
  };

  const onOpenPrintView = useCallback(() => {
    if (!gridRef.current || !projectDetails) {
      message.error("Unable to open print view. Grid data is not available.");
      return;
    }

    try {
      sessionStorage.clear();

      const contextData = {
        projectDetails,
        projectId,
        isGridCellEditable: false,
        editPermission: false,
        // no-op stubs stored into contextData, which is JSON.stringify'd into
        // sessionStorage below (functions are dropped) — never invoked here.
        /* v8 ignore start */
        onEditCell: () => {},
        refetchLogData: () => {},
        /* v8 ignore stop */
        featureHeaderMap: pageData.bidPackageHeaderMap
      };

      const initialState = gridRef.current.api.getState();
      const printViewState: BidPackageLogPrintViewState = {
        pageId: "bid-package-log",
        title: `${
          projectDetails?.name || "Project"
        } - Bid Package Log - Exported from ConstructivIQ on ${DateUtils.dateTimeObj().format(
          `M/D [at] h:mma [${DateUtils.dateTimeObj().format("z")}]`
        )}`,
        initialState,
        context: contextData,
        skipColumnIds: ["action"],
        headerHeight: gridRef.current.props.headerHeight,
        rowHeight: gridRef.current.props.rowHeight,
        conditionalsFormatting: conditionalFormattingRulesRef.current
      };

      sessionStorage.setItem(
        PRINT_VIEW_STORAGE_KEY,
        JSON.stringify(printViewState)
      );

      window.open(`/project/${projectId}/printView`, "_blank");
    } catch (error) {
      console.error("Error opening bid package log print view:", error);
      message.error("An error occurred while opening print view.");
    }
  }, [projectDetails, projectId, pageData.bidPackageHeaderMap]);

  const headerItems = () => {
    const menus = [] as JSX.Element[];
    if (!bidPackageListFilter)
      menus.push(
        <FilterChipComponent
          key="PreconstructionLogPage_FilterChip"
          gridRef={gridRef}
          ref={filterChipRef}
          onSearchTextClear={() => setSearchText("")}
        />
      );
    if (bulkEditButton) menus.push(bulkEditButton);
    menus.push(
      <CiqAgSearch
        key="PreconstructionLogPage_search"
        gridRef={gridRef}
        value={searchText}
        onSearch={onSearch}
      />
    );

    menus.push(
      <ColumnConfigViews
        key="PreconstructionLogPage_ColumnConfigViews"
        featureId={EFeaturePageId.BID_PACKAGE}
        setFilterStateFromData={applyColumnConfig}
        getColumnStateFromGrid={() => {
          if (gridRef.current) {
            return UtilColumnConfig.getColumnStateFromGrid({
              gridRef: {
                api: gridRef.current!.api
              },
              isIncludeSearchText: false,
              conditionals_formatting: conditionalFormattingRulesRef.current
            });
          }
          return null;
        }}
        afterApplyFilterCallBack={refreshSidebarTreeLayout}
      />
    );

    const exportButtonDisabled =
      !pageData.bidPackageList?.bid_package_list_func || !!isProjectArchived;

    const exportOptions: ExportFormatOption[] = [
      {
        key: "export_csv",
        title: "CSV",
        testId: "bid-package-header-export-csv"
      }
    ];
    if (isExcelExportEnabled) {
      exportOptions.push({
        key: "export_excel",
        title: "Excel (.xlsx)",
        isExcel: true,
        disabled: exportButtonDisabled,
        testId: "bid-package-header-export-excel"
      });
    }
    if (isPrintViewEnabled) {
      exportOptions.push({
        key: "print_view",
        title: "PDF/Print",
        testId: "bid-package-header-print-view"
      });
    }

    const onProceedExport = ({ key, excelOptions }: ExportSelection) => {
      if (key === "export_excel") {
        if (!exportButtonDisabled && excelOptions) onExportExcel(excelOptions);
        return;
      }
      if (key === "print_view") {
        if (gridRef.current)
          PrintViewValidationModal({
            gridApi: gridRef.current.api,
            onProceed: onOpenPrintView,
            skipColumnIds: ["action", "ag-Grid-SelectionColumn", "designScope"]
          });
        return;
      }
      onExportCsv();
    };

    menus.push(
      <ExportOptionsPopover
        key="PreconstructionLogPage_export"
        options={exportOptions}
        onProceed={onProceedExport}
        excelTableName={projectDetails?.name || "Project"}
        showIncludeComments={false}
        disabled={exportButtonDisabled}
        buttonType="default"
        buttonTestId="index-button-export"
      />
    );

    menus.push(
      <AddEntityLogButton
        key="PreconstructionLogPage_add"
        buttonLabel="Add New"
        disabled={cannotWriteAccessOfBidPackage}
        disabledTitle={ErrorMessages.PermissionNotGranted}
        enabledTitle="Create New Bid Package"
        data-testid="index-button-new-bidpackage"
        items={[
          {
            key: "create_new",
            title: "Manually create a Bid Package",
            description: "Create a single Bid Package manually",
            onClick: () => setShowDrawer(true)
          },
          {
            key: "import_log",
            title: "Import Bid Packages",
            description: "Upload a CSV to add or update Bid Packages",
            onClick: () => setShowImportLogDrawer(true)
          }
        ]}
      />
    );

    return menus;
  };

  const onEditCell = useCallback(
    (params: any) => {
      gridRef?.current?.api?.setFocusedCell(params.rowIndex, params.colKey);
      gridRef?.current?.api?.startEditingCell({
        rowIndex: params.rowIndex,
        colKey: params.colKey
      });
    },
    [gridRef]
  );

  const statusBadge = useMemo(
    () =>
      getGridStatusBudge(
        {
          title: "Bid Packages",
          totalCount: pageData.bidPackageList?.bid_package_list_func?.length,
          filterStats: statsData
        },
        onAllBidPackagesClick
      ),
    [
      onAllBidPackagesClick,
      pageData.bidPackageList?.bid_package_list_func?.length,
      statsData
    ]
  );

  const entityName = (
    <div className="flex items-center">
      <CiqIconBox feature="BID_PACKAGE" size={20} padding={8} />
      <div className="flex min-w-0 flex-col pl-3">
        <div className="flex items-center space-x-2 text-black">
          <div className="text-black text-xl font-semibold">Bid Packages</div>
          <Tag>Beta</Tag>
          {statusBadge ? (
            <div
              className="flex shrink-0 items-center"
              data-testid="feature-entity-status-badge"
            >
              {statusBadge}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );

  // Rendered by the grid's no-rows overlay, so it only appears once loading is
  // done. An empty log gets the blank state; an empty search/filter result
  // gets a plain "no results" message.
  const noRowsOverlay =
    pageData.bidPackageList?.bid_package_list_func?.length === 0 ? (
      <BlankState
        title="No Bid Packages added"
        description="Create or Import Bid Package items list to start."
        primaryAction={{
          label: "Import Bid Package",
          onClick: () => setShowImportLogDrawer(true),
          disabled: cannotWriteAccessOfBidPackage,
          disabledTitle: ErrorMessages.PermissionNotGranted,
          "data-testid": "bid-package-blank-import"
        }}
        secondaryActions={[
          {
            label: "Add New",
            onClick: () => setShowDrawer(true),
            disabled: cannotWriteAccessOfBidPackage,
            disabledTitle: ErrorMessages.PermissionNotGranted,
            "data-testid": "bid-package-blank-add-new"
          }
        ]}
        data-testid="bid-package-blank-state"
      />
    ) : (
      <BlankState
        title="No Bid Packages match your search or filters"
        data-testid="bid-package-no-results"
      />
    );

  return (
    <>
      {contextHolder}
      <CollapsibleSidebarLayout
        sidebarState={[isSidebarOpen, () => {}]}
        sidebarWidth="420px"
        sidebarHeader={
          <ErrorBoundary>
            <PreconstructionLogSidebarHeader
              title={sidebarTitle}
              caption={sidebarMetadata?.itemDetails?.title || ""}
              onClose={() => {
                setIsSidebarOpen(false);
                sidebarAction({
                  feature: "",
                  action: "close",
                  itemDetailsToSet: null
                });
              }}
            />
          </ErrorBoundary>
        }
        sidebarContent={
          sidebarMetadata?.itemDetails ? (
            <ErrorBoundary>
              <FeatureCommentsTabComponent
                key={sidebarMetadata.itemDetails.id}
                permissions={permissionsForComments}
                featureKey="feature_instance_id"
                featureId={sidebarMetadata.itemDetails.id}
                isAttachementFeatureEnabled={isAttachementFeatureEnabled}
                auditFeatureType="BID_PACKAGE"
                data-testid="bid-package-comments-tab"
                paddingX="px-6"
                onCommentsCountChange={onCommentsCountChange}
              />
            </ErrorBoundary>
          ) : null
        }
        mainContent={
          <GridNoRowsOverlayContext.Provider value={noRowsOverlay}>
            <CiqGridViewWrapper
              headerInfo={{
                entityName,
                items: headerItems()
              }}
              gridInfo={{
                gridRef,
                columnDefs: agGridData.columnDefs,
                rowData: pageData.bidPackageList?.bid_package_list_func,
                defaultColDef: conditionalFormattingDefaultColDef,
                getMainMenuItems: getConditionalFormattingHeaderMenuItems,
                rowHeight: 62,
                getRowId, // After refetch the rowdata ,It help to persist Scroll Bar
                excelStyles: mergedExcelStyles,
                readOnlyEdit: true,
                singleClickEdit: true,
                stopEditingWhenCellsLoseFocus: true,
                onCellEditRequest: (event: any) => {
                  onCellEditRequest(event);
                },
                onFirstDataRendered,
                onModelUpdated: () => {
                  navWriter.onModelUpdated();
                },
                onSortChanged: () => {
                  if (isFirstDataRendered && !bidPackageListFilter)
                    pageData.onSaveColumnState();
                  navWriter.onSortChanged();
                },
                onFilterChanged: (event: any) => {
                  filterChipRef.current?.onFilterApplied();
                  setStatsData(
                    calculateLogPageStats({
                      gridRef,
                      ListFilter: bidPackageListFilter,
                      featureTypeId: FeatureTypes.BID_PACKAGE
                    })
                  );
                  updateGridSelectionOnFilter(event, gridRef);
                  if (isFirstDataRendered && !bidPackageListFilter)
                    pageData.onSaveColumnState();
                  navWriter.onFilterChanged();
                },
                onColumnVisible: (e) => {
                  if (e.api && e.source === "toolPanelUi") {
                    pageData.onSaveColumnState();
                  }
                },
                onDragStopped: (e) => {
                  const classNames = [
                    "ag-header-cell-resize",
                    "ag-tool-panel-horizontal-resize"
                  ];
                  if (e.api && !classNames.includes(e.target.className)) {
                    pageData.onSaveColumnState();
                    if (
                      pageData.bidPackageList?.bid_package_list_func?.length
                    ) {
                      setTimeout(() => {
                        applySidebarTreeLayout();
                      }, 0);
                    }
                  }
                },
                onToolPanelVisibleChanged: (e: any) => {
                  if (e?.key === "columns" && e?.visible) {
                    setTimeout(() => applySidebarTreeLayout(), 0);
                  }
                },
                onNewColumnsLoaded: () => {
                  // ag-grid resyncs the columns tool panel with the (flat) grid
                  // columnDefs whenever new columns load (e.g. after inline edit
                  // refetches the list and rebuilds milestonesColumns) — that
                  // overwrites our setColumnLayout. Re-apply the tree so the
                  // Milestones / Durations grouping survives data-driven
                  // columnDefs updates.
                  setTimeout(() => applySidebarTreeLayout(), 0);
                },
                context: {
                  conditionalFormattingRules,
                  onConditionalFormattingRulesChange,
                  conditionalFormattingColumnConfig: {
                    risk: {
                      filterType: "set",
                      options: [
                        RiskLevelType.High,
                        RiskLevelType.Medium,
                        RiskLevelType.Low
                      ]
                    },
                    milestone_state: {
                      filterType: "set",
                      options: pageData.statusMilestoneOptions ?? []
                    }
                  },
                  onEditCell,
                  isGridCellEditable: agGridData.isGridCellEditable,
                  refetchLogData: pageData.refetchBidPackageList,
                  editPermission: !cannotWriteAccessOfBidPackage,
                  featureType: FeatureTypes.BID_PACKAGE,
                  featureHeaderMap: pageData.bidPackageHeaderMap,
                  onCommentClick,
                  isProjectArchived: !!isProjectArchived,
                  getScopeItemsForBid: (id: string) =>
                    scopeItemsByBidRef.current[id] ?? [],
                  onViewScope: (bidPackageId: string, label: string) =>
                    setScopeCtx({ bidPackageId, label })
                },
                rowClassRules,
                onSelectionChanged: () => {
                  setSelectedRows(
                    gridRef?.current?.api?.getSelectedRows() || null
                  );
                },
                loading: !isFirstDataRendered,
                onColumnResized: (params: any) => {
                  if (params.finished) {
                    // User has finished resizing, do something like save column state
                    pageData.onSaveColumnState();
                  }
                },
                noRowsOverlayComponent: GridNoRowsOverlay,
                disableResizingAutoWidth: true
              }}
            />
          </GridNoRowsOverlayContext.Provider>
        }
        placement="right"
      />

      {showDrawer && (
        <CreateBidPackageComponent
          setDrawerOpen={setShowDrawer}
          showDrawerOpen={showDrawer}
          modelTitle="Create Bid Package"
          onBidPackageCreated={(title: string, id: string) => {
            setShowDrawer(false);
            openNotification(id, title);
            pageData.refetchBidPackageList();
          }}
        />
      )}

      {showImportLogDrawer && (
        <ImportFeatureLogs
          open={showImportLogDrawer}
          featureType={FeatureTypes.BID_PACKAGE}
          entityLabelSingular="Bid Package"
          entityLabelPlural="Bid Packages"
          showWorkflowTemplates
          standardTemplate={{
            headers: [
              "Number",
              "Title",
              "Description",
              "Wave",
              "Estimated Value",
              "Number of Bidders"
            ],
            fileName: "Sample_Bid_Package_Log.csv"
          }}
          onCancel={() => setShowImportLogDrawer(false)}
          onComplete={() => pageData.refetchBidPackageList()}
        />
      )}

      {bulkEditMode === "details" && (
        <ErrorBoundary>
          <BulkEditBidPackageDetails
            showEdit
            setShowEdit={setBulkEditMode}
            selectedRows={selectedRows!}
            onUpdateSuccess={() => {
              setBulkEditMode(null);
              setSelectedRows(null);
              gridRef.current?.api?.deselectAll();
              setTimeout(() => {
                pageData.refetchBidPackageList();
              });
            }}
          />
        </ErrorBoundary>
      )}

      {selectedRows && bulkEditMode === "status" && (
        <BulkFeatureEditStatus
          showEdit
          setShowEdit={setBulkEditMode}
          selectedRows={selectedRows}
          mapRowToWorkflowItem={mapPackageRowToWorkflowItem}
          enablePredecessorExclusion
          entityLabelSingular="Bid Package"
          entityLabelPlural="Bid Packages"
          testIdPrefix="bulkfeatureeditbidpackagestatus"
          icon={
            <BidPackageCiqIcon
              size={20}
              className="fill-ciq-gray-900 pr-[5px]"
              opacity="1"
            />
          }
          onUpdateDone={() => {
            setBulkEditMode(null);
            gridRef?.current?.api?.deselectAll();
            pageData.refetchBidPackageList();
          }}
        />
      )}

      {scopeCtx && (
        <BidScopeListDrawer
          open={Boolean(scopeCtx)}
          bidPackageId={scopeCtx.bidPackageId}
          label={scopeCtx.label}
          onClose={() => setScopeCtx(null)}
        />
      )}

      {selectedRows && bulkEditMode === "onsite_estimate" && (
        <BulkEditOnsiteEstimateModal
          selectedRows={selectedRows}
          label={onsiteEstimateLabel}
          deadlineLabel={
            pageData.bidPackageHeaderMap?.final_deadline_date || "Deadline"
          }
          onCancel={() => setBulkEditMode(null)}
          onApplied={() => {
            setBulkEditMode(null);
            gridRef?.current?.api?.deselectAll();
            pageData.refetchBidPackageList();
          }}
        />
      )}

      {selectedRows && bulkEditMode === "deadline_date" && (
        <BulkEditDeadlineDateModal
          showEdit
          setShowEdit={setBulkEditMode}
          selectedRows={selectedRows}
          columnHeaderMap={pageData.bidPackageHeaderMap}
          featureTypeId={FeatureTypes.BID_PACKAGE}
          onUpdateDone={() => {
            setBulkEditMode(null);
            gridRef?.current?.api?.deselectAll();
            pageData.refetchBidPackageList();
          }}
        />
      )}

      {selectedRows && bulkEditMode === "durations" && (
        <Modal
          title="Bulk Edit Durations"
          className="custom-drawer with-padding-fix"
          style={SidepanelStyles.style}
          width="calc(100vw - 200px)"
          open
          onCancel={() => setBulkEditMode(null)}
          destroyOnHidden
          keyboard={false}
          footer={[
            <div className="flex w-full space-x-3 justify-end" key={100}>
              <Button
                key={101}
                onClick={() => {
                  setBulkEditMode(null);
                  gridRef?.current?.api?.deselectAll();
                }}
                data-testid="bulk-durations-close"
              >
                Close
              </Button>
            </div>
          ]}
        >
          <ErrorBoundary>
            <FeatureBulkEditDurationsNewModel
              selectedRows={durationRows}
              featureTypeId={FeatureTypes.BID_PACKAGE}
              // Deliberately does NOT close or deselect: this modal stays open
              // after a save, and emptying the selection would leave the open
              // panel reading "Selected Bid Packages: 0".
              onUpdateDone={() => {
                pageData.refetchBidPackageList();
              }}
            />
          </ErrorBoundary>
        </Modal>
      )}
    </>
  );
}

function PreconstructionLogPage() {
  return (
    <PreconstructionLogProvider>
      <PreconstructionLogPageContent />
    </PreconstructionLogProvider>
  );
}

export default PreconstructionLogPage;
